import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { NeedCreate } from '../../../../../js/api/models/NeedCreate';
import type { NeedDetail } from '../../../../../js/api/models/NeedDetail';
import type { PatchedNeedUpdate } from '../../../../../js/api/models/PatchedNeedUpdate';
import { WorkTypeEnum } from '../../../../../js/api/models/WorkTypeEnum';
import { HealthcareEntitiesService } from '../../../../../js/api/services/HealthcareEntitiesService';
import { StaffingService } from '../../../../../js/api/services/StaffingService';
import { UnsavedChangesFormPrompt } from '../../../../../js/app/components/UnsavedChangesFormPrompt';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { CheckButton } from '../../../components/CheckButton';
import { DropdownSelect } from '../../../components/dropdown-select';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Label, Fieldset, Field, Description } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { PageHeader } from '../../../components/PageHeader';
import { SectionHeader } from '../../../components/SectionHeader';
import { ShiftTypeSelector } from '../../../components/shift-type-selector';
import { Spinner } from '../../../components/spinner';
import StatusPill from '../../../components/StatusPill';
import { Textarea } from '../../../components/textarea';
import { WorkflowScopes } from '../../../constants/workflows';
import { useContextBarConfig } from '../../../contexts/ContextBarContext';
import {
  useCodeDefinitionsByCategories,
  useCodeDefinitionsAsOptions,
} from '../../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useStaffOrgFilter } from '../../../hooks/useStaffOrgFilter';
import { parseApiLocalDate, parseFormDate } from '../../../utils/date';
import { reactSelectReset } from '../../../utils/utils';
import {
  NEED_QUERY_KEY,
  ORGANIZATION_NEEDS_LIST_QUERY_KEY,
  STAFF_NEEDS_LIST_QUERY_KEY,
  NEED_STATUS_HISTORY_QUERY_KEY,
  NEED_CHANGE_HISTORY_QUERY_KEY,
} from './queryKeys';
import 'react-datepicker/dist/react-datepicker.css';

const SECTION_CLASSES = '';

const needFormSchema = z.object({
  // Basic Information
  facility: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .refine((v) => v != null, { message: 'Home facility is required' }),
  description: z.string().max(255).optional(),
  target_on: z.date().nullable(),
  alternate_facilities: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    })
  ),
  urgency_level: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  justification: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  num_shifts: z.coerce.number().min(0).nullable().optional(),
  job_requisition_key: z.string().optional(),
  job_requisition_url: z.string().optional(),

  // Professional Requirements
  specialty: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .refine((v) => v != null, { message: 'Specialty is required' }),
  degrees: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .min(1, { message: 'At least one degree must be selected' }),
  work_types: z.array(z.string()),

  shift_rate_types: z.array(z.string()),
  max_rate_day: z.coerce.number().min(0).nullable(),
  max_rate_night: z.coerce.number().min(0).nullable(),
  max_rate_call: z.coerce.number().min(0).nullable(),
  max_rate_24h: z.coerce.number().min(0).nullable(),
});

type NeedFormData = z.infer<typeof needFormSchema>;

const DEFAULT_FORM_VALUES: Partial<NeedFormData> = {
  facility: undefined,
  description: '',
  target_on: null,
  alternate_facilities: [],
  urgency_level: undefined,
  justification: undefined,
  num_shifts: null,
  job_requisition_key: '',
  job_requisition_url: '',
  specialty: undefined,
  degrees: [
    { value: 'MD', label: 'MD' },
    { value: 'DO', label: 'DO' },
  ],
  work_types: Object.values(WorkTypeEnum),
  shift_rate_types: [],
  max_rate_day: null,
  max_rate_night: null,
  max_rate_call: null,
  max_rate_24h: null,
};

interface NeedFormProps {
  readOnly?: boolean;
  needId?: string;
  forceEditMode?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  renderHeader?: boolean;
  onHeaderRender?: (headerProps: { title: string; buttonHtml: React.ReactNode }) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const NeedForm: React.FC<NeedFormProps> = ({
  readOnly: forceReadOnly,
  needId: propNeedId,
  forceEditMode: forceEditModeProp,
  onSuccess: onSuccessCallback,
  onCancel: onCancelCallback,
  renderHeader = true,
  onHeaderRender,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user, audienceIsStaff } = useContext(SessionContext);
  const queryClient = useQueryClient();
  const staffOrgFilter = useStaffOrgFilter();
  const canCreateNeed = useHasPermission('create__staffing__need');
  const canUpdateNeed = useHasPermission('update__staffing__need');

  const needListPath = audienceIsStaff ? '/staff/needs' : '/app/organization/needs';
  const needDetailPath = (id: string) =>
    audienceIsStaff ? `/staff/needs?id=${id}` : `/app/organization/needs?id=${id}`;

  const needId = propNeedId || params.needId;
  const urlPath = location.pathname;

  const isCreateMode = urlPath.includes('/new');
  const isEditMode = forceEditModeProp || urlPath.includes('/edit');
  const isViewMode = !isCreateMode && !isEditMode && Boolean(needId);

  useContextBarConfig(
    isCreateMode
      ? {
          buttonLabel: 'Back to Needs',
          buttonUrl: '/app/organization/needs',
        }
      : null
  );

  const form = useForm<NeedFormData>({
    resolver: zodResolver(needFormSchema),
    defaultValues: DEFAULT_FORM_VALUES as NeedFormData,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
    register,
  } = form;

  const watchedHomeFacility = watch('facility');

  const formRef = useRef<HTMLFormElement>(null);
  const [filteredFacilities, setFilteredFacilities] = useState<any[]>([]);
  const [selectedAdditionalFacility, setSelectedAdditionalFacility] = useState<any | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data: codeDefinitions, isLoading: isCodeDefinitionsLoading } =
    useCodeDefinitionsByCategories([
      'WorkType',
      'Degree',
      'Specialty',
      'ShiftRateTypes',
      'ShiftRateTypeIcons',
      'UrgencyLevel',
      'JustificationType',
    ]);

  // Use the dedicated hook for options that go directly into Select components
  const { data: urgencyLevelSelectOptions } = useCodeDefinitionsAsOptions('UrgencyLevel');
  const { data: justificationSelectOptions } = useCodeDefinitionsAsOptions('JustificationType');
  const { data: specialtySelectOptions } = useCodeDefinitionsAsOptions('Specialty');

  const workTypeOptions = useMemo(
    () =>
      (codeDefinitions?.WorkType || []).map((def) => ({
        value: def.code,
        description: def.title,
      })),
    [codeDefinitions?.WorkType]
  );

  const degreeSelectOptions = useMemo(
    () =>
      (codeDefinitions?.Degree || []).map((def) => ({
        value: def.code,
        label: def.title,
        is_active: def.is_active,
      })),
    [codeDefinitions?.Degree]
  );

  const shiftTypeOptions = useMemo(() => {
    const shiftTypes = codeDefinitions?.ShiftRateTypes || [];
    const shiftIcons = codeDefinitions?.ShiftRateTypeIcons || [];
    return shiftTypes.map((type) => {
      const icon = shiftIcons.find((i) => i.code === type.code);
      return {
        key: type.code.toLowerCase(),
        label: type.title,
        icon: <i className={`${icon?.title || 'fas fa-question'} tw:w-6 tw:h-6`} />,
      };
    });
  }, [codeDefinitions?.ShiftRateTypes, codeDefinitions?.ShiftRateTypeIcons]);

  const { data: existingNeed, isLoading: isLoadingNeed } = useQuery({
    queryKey: [NEED_QUERY_KEY, needId],
    queryFn: () => StaffingService.staffingNeedsRetrieve({ id: needId! }),
    enabled: (isEditMode || isViewMode) && Boolean(needId),
  });

  // Facility list scope:
  // - edit/view: source need's healthcare_entity (may differ from staff acting-as)
  // - create: staff acting-as id; HCO reps fall through to backend security-context scoping
  const facilitiesHealthcareEntityFilter = existingNeed?.healthcare_entity?.id || staffOrgFilter;
  const { data: facilities } = useQuery({
    queryKey: [
      'healthcareEntityFacilities',
      'list',
      { healthcareEntity: facilitiesHealthcareEntityFilter ?? null },
    ],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesList({
        limit: 1000,
        healthcareEntity: facilitiesHealthcareEntityFilter,
      }),
    enabled: !(isEditMode || isViewMode) || Boolean(existingNeed),
  });

  const needDisplayTitle = (existingNeed?.code || '').trim() || 'Need Details';
  const documentTitle = isCreateMode ? 'Create Need' : needDisplayTitle;

  useDocumentTitle(documentTitle);

  const isReadOnly = React.useMemo(() => {
    if (forceReadOnly !== undefined) {
      return forceReadOnly;
    }
    if (isViewMode) {
      return true;
    }
    if (isEditMode) {
      return !canUpdateNeed;
    }
    return !canCreateNeed;
  }, [forceReadOnly, isViewMode, isEditMode, canUpdateNeed, canCreateNeed]);

  useEffect(() => {
    if (facilities?.results) {
      const filtered = watchedHomeFacility
        ? facilities.results.filter((facility) => facility.id !== watchedHomeFacility.id)
        : facilities.results;
      setFilteredFacilities(filtered);
    }
  }, [facilities, watchedHomeFacility]);

  useEffect(() => {
    if (watchedHomeFacility) {
      const currentAdditionalFacilities = watch('alternate_facilities') || [];
      const filtered = currentAdditionalFacilities.filter(
        (f: any) => f.id !== watchedHomeFacility.id
      );
      setValue('alternate_facilities', filtered);
    }
  }, [watchedHomeFacility, watch, setValue]);

  // Create mode
  useEffect(() => {
    if (isCreateMode) {
      reset(DEFAULT_FORM_VALUES as NeedFormData);
    }
  }, [isCreateMode, reset]);

  // API Transformation Functions
  // TODO: at some point, we need to map string values from the api to CodeDef objects
  // does it need to be here?
  const transformFacilityApiToForm = (value: any) => value || null;

  const transformDateApiToForm = (value: string | null | undefined) => parseApiLocalDate(value);

  const transformDegreesApiToForm = (value: string[]): Array<{ value: string; label: string }> =>
    value?.map((degree) => ({ value: degree, label: degree })) || [
      { value: 'MD', label: 'MD' },
      { value: 'DO', label: 'DO' },
    ];

  const transformSpecialtyApiToForm = (value: string) => {
    if (!value) return undefined;
    return specialtySelectOptions?.find((s) => s.value === value) || { value, label: value };
  };

  const transformUrgencyLevelApiToForm = (value: string | null | undefined) => {
    if (!value) return null;
    return urgencyLevelSelectOptions?.find((u) => u.value === value) || { value, label: value };
  };

  const transformJustificationApiToForm = (value: string | null | undefined) => {
    if (!value) return null;
    return justificationSelectOptions?.find((j) => j.value === value) || { value, label: value };
  };

  const apiToForm = React.useCallback((apiData: NeedDetail): Partial<NeedFormData> => {
    return {
      facility: transformFacilityApiToForm(apiData.facility),
      description: apiData.description || '',
      target_on: transformDateApiToForm(apiData.target_on),
      alternate_facilities: (apiData.alternate_facilities as any) || [],
      urgency_level: transformUrgencyLevelApiToForm(apiData.urgency_level),
      justification: transformJustificationApiToForm(apiData.justification),
      num_shifts: apiData.num_shifts || null,
      job_requisition_key: apiData.job_requisition_key || '',
      job_requisition_url: apiData.job_requisition_url || '',
      specialty: transformSpecialtyApiToForm(apiData.specialty),
      degrees: transformDegreesApiToForm(apiData.degrees || []),
      work_types: apiData.work_types || [],
      shift_rate_types: apiData.shift_rate_types || [],
      max_rate_day: apiData.max_rate_day || null,
      max_rate_night: apiData.max_rate_night || null,
      max_rate_call: apiData.max_rate_call || null,
      max_rate_24h: apiData.max_rate_24h || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edit/View Mode - Load existing need data
  useEffect(() => {
    if (
      (isEditMode || isViewMode) &&
      existingNeed &&
      facilities &&
      specialtySelectOptions &&
      specialtySelectOptions.length > 0
    ) {
      const formData = apiToForm(existingNeed);
      reset(formData);
    }
  }, [isEditMode, isViewMode, existingNeed, facilities, specialtySelectOptions, reset, apiToForm]);

  const formToApiUpdate = (formData: NeedFormData): PatchedNeedUpdate => {
    // Regular update payload (no status fields)
    return {
      facility: formData.facility?.id || undefined,
      description: formData.description || undefined,
      target_on: parseFormDate(formData.target_on) || undefined,
      alternate_facilities: formData.alternate_facilities as any,
      urgency_level: (formData.urgency_level?.value || null) as any,
      justification: (formData.justification?.value || null) as any,
      num_shifts: formData.num_shifts || undefined,
      job_requisition_key: formData.job_requisition_key || undefined,
      job_requisition_url: formData.job_requisition_url || undefined,
      specialty: formData.specialty?.value as any,
      degrees: formData.degrees.map((degree) => degree.value) as any,
      work_types: formData.work_types as any,
      shift_rate_types: formData.shift_rate_types as any,
      max_rate_day: formData.max_rate_day || undefined,
      max_rate_night: formData.max_rate_night || undefined,
      max_rate_call: formData.max_rate_call || undefined,
      max_rate_24h: formData.max_rate_24h || undefined,
    };
  };

  const formToApiCreate = (formData: NeedFormData): NeedCreate => {
    const payload: any = {
      id: '',
      facility: formData.facility?.id || '',
      target_on: parseFormDate(formData.target_on) || undefined,
      degrees: formData.degrees.map((degree) => degree.value) as never[],
      specialty: formData.specialty?.value as never,
      urgency_level: (formData.urgency_level?.value || null) as never,
      justification: (formData.justification?.value || null) as never,
      num_shifts: formData.num_shifts || undefined,
      healthcare_entity: user?.healthcare_entity_id || staffOrgFilter || '',
      alternate_facilities: formData.alternate_facilities,
      description: formData.description,
      work_types: formData.work_types as never[],
      shift_rate_types: formData.shift_rate_types as never[],
      max_rate_day: formData.max_rate_day || undefined,
      max_rate_night: formData.max_rate_night || undefined,
      max_rate_call: formData.max_rate_call || undefined,
      max_rate_24h: formData.max_rate_24h || undefined,
      job_requisition_key: formData.job_requisition_key || undefined,
      job_requisition_url: formData.job_requisition_url || undefined,
    };

    return payload as NeedCreate;
  };

  const onInvalid = (formErrors: Record<string, { message?: string }>) => {
    const messages: string[] = Object.values(formErrors)
      .map((e) => e?.message)
      .filter((msg): msg is string => Boolean(msg));

    Object.entries(formErrors).forEach(([fieldName, error]) => {
      if (error?.message) {
        setError(fieldName as keyof NeedFormData, {
          type: 'manual',
          message: error.message,
        });
      }
    });

    if (messages.length) {
      toast.error(
        <div>
          <div className="tw:font-medium tw:mb-1">Please fix the following errors:</div>
          <ul className="tw:list-disc tw:list-inside">
            {messages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  const handleCancel = useCallback(() => {
    if (onCancelCallback) {
      onCancelCallback();
    } else {
      if (isEditMode && needId) {
        navigate(needDetailPath(needId));
      } else {
        navigate(needListPath);
      }
    }
  }, [onCancelCallback, isEditMode, needId, navigate, needDetailPath, needListPath]);

  const handleEdit = useCallback(() => {
    if (needId) {
      navigate(`/app/organization/needs/${needId}/edit`);
    }
  }, [needId, navigate]);

  const createNeedMutation = useMutation({
    mutationFn: async (data: NeedCreate) => {
      return await StaffingService.staffingNeedsCreate({
        requestBody: data as NeedCreate,
      });
    },
    onSuccess: (createdNeed) => {
      toast.success('Need created successfully');
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_NEEDS_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STAFF_NEEDS_LIST_QUERY_KEY] });
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        navigate(needDetailPath(createdNeed.id));
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error creating need' });
    },
  });

  const updateNeedMutation = useMutation({
    mutationFn: async (data: PatchedNeedUpdate) => {
      return await StaffingService.staffingNeedsPartialUpdate({
        id: needId!,
        requestBody: data as PatchedNeedUpdate,
      });
    },
    onSuccess: async (data: NeedDetail) => {
      toast.success('Need updated successfully');
      queryClient.setQueryData([NEED_QUERY_KEY, needId], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [NEED_QUERY_KEY, needId] }),
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_NEEDS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [STAFF_NEEDS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [NEED_STATUS_HISTORY_QUERY_KEY, needId],
        }),
        queryClient.invalidateQueries({
          queryKey: [NEED_CHANGE_HISTORY_QUERY_KEY, needId],
        }),
      ]);
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        navigate(needDetailPath(needId!));
      }
    },
    onError: (error: Error) => {
      console.log('updateNeedMutation error', error);
      handleApiError(error, { messagePrefix: 'Error updating need' });
    },
  });

  const handleSave = handleSubmit(async () => {
    const currentValues = watch();

    if (isEditMode && needId) {
      // Edit mode: Update all fields
      const regularUpdateData = formToApiUpdate(currentValues);
      await updateNeedMutation.mutateAsync(regularUpdateData);
    } else {
      // Create mode: Use create endpoint
      const createData = formToApiCreate(currentValues);
      createNeedMutation.mutate(createData);
    }

    reset(watch());
  }, onInvalid);

  const isLoading =
    isCodeDefinitionsLoading || ((isEditMode || isViewMode) && (isLoadingNeed || !existingNeed));

  const actionButtonsElement = useMemo(
    () => (
      <>
        {isViewMode && canUpdateNeed && (
          <Button type="button" color="brand-green-outline-on-dark" onClick={handleEdit}>
            Edit
          </Button>
        )}
        {(isEditMode || isCreateMode) && !isReadOnly && (
          <>
            <Button type="button" color="zinc-outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" color="brandgreen1" onClick={handleSave}>
              Save
            </Button>
          </>
        )}
        {!isEditMode && !isViewMode && !isCreateMode && (
          <>
            <Button type="button" color="zinc-outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" color="brandgreen1" onClick={handleSave}>
              Create Need
            </Button>
          </>
        )}
      </>
    ),
    [
      isViewMode,
      canUpdateNeed,
      isEditMode,
      isCreateMode,
      isReadOnly,
      handleEdit,
      handleCancel,
      handleSave,
    ]
  );

  const headerTitle = isEditMode
    ? 'Edit Need: ' + needDisplayTitle
    : isViewMode
      ? 'Need: ' + needDisplayTitle
      : 'Create Need';

  // Expose header props via callback if provided
  useEffect(() => {
    if (!isLoading && onHeaderRender) {
      onHeaderRender({
        title: headerTitle,
        buttonHtml: actionButtonsElement,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, onHeaderRender, headerTitle, actionButtonsElement]);

  return (
    <>
      {isLoading && (
        <div className="tw:flex tw:items-center tw:justify-center tw:min-h-screen">
          <div className="tw:text-center">
            <Spinner size="xl" className="tw:mx-auto tw:mb-4" />
            <p className="tw:text-zinc-600 tw:dark:text-zinc-400">Loading...</p>
          </div>
        </div>
      )}
      {!isLoading && (
        <>
          <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
            {/* Main content area - takes 2/3 when drawer open, full width when closed */}
            <div
              className={clsx(
                'tw:flex tw:flex-col tw:transition-all tw:duration-300 tw:overflow-hidden',
                isDrawerOpen && needId ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
              )}
            >
              {renderHeader && (
                <PageHeader
                  className="tw:border-b-0"
                  title={headerTitle}
                  buttonHtml={actionButtonsElement}
                />
              )}
              <main className="tw:flex-1 tw:overflow-y-auto tw:mx-auto tw:px-6 tw:pb-8 tw:w-full">
                <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
                  <div className="tw:space-y-6">
                    {/* Basic Information Section */}
                    <section className={SECTION_CLASSES}>
                      <div className="tw:flex tw:items-start tw:justify-between">
                        <SectionHeader
                          headerText="Basic Information"
                          icon={
                            <i className="fas fa-info-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                          }
                        />
                        {/* {(isViewMode || isEditMode) && existingNeed?.current_status && (
                      <StatusPill
                        scope={WorkflowScopes.Need}
                        status={existingNeed.current_status}
                      />
                    )} */}
                      </div>
                      <Fieldset>
                        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                          <Field>
                            <Label>
                              Home Facility<span className="tw:text-stoplight-danger">*</span>
                            </Label>
                            <Controller
                              name="facility"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  styles={reactSelectReset}
                                  placeholder="Start typing or select from list..."
                                  options={
                                    facilities?.results?.map((facility) => ({
                                      value: facility,
                                      label: facility.name,
                                    })) || []
                                  }
                                  value={
                                    field.value
                                      ? {
                                          value: field.value,
                                          label: field.value.name,
                                        }
                                      : null
                                  }
                                  onChange={(selectedOption) =>
                                    field.onChange(selectedOption?.value || null)
                                  }
                                  isDisabled={isReadOnly}
                                />
                              )}
                            />
                            {errors.facility && (
                              <FieldValidationErrorMessage message={errors.facility.message} />
                            )}
                          </Field>
                          <Field>
                            <Label>Target Date</Label>
                            <Controller
                              name="target_on"
                              control={control}
                              render={({ field }) => (
                                <ReactDatePicker
                                  className="datepicker-input"
                                  wrapperClassName="datepicker-wrapper"
                                  selected={field.value}
                                  onChange={field.onChange}
                                  placeholderText="Select target date"
                                  disabled={isReadOnly}
                                />
                              )}
                            />
                          </Field>
                        </div>
                        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                          <Field>
                            <Label>Justification</Label>
                            <Controller
                              name="justification"
                              control={control}
                              render={({ field }) => (
                                <DropdownSelect
                                  allOptions={justificationSelectOptions}
                                  value={field.value}
                                  onChange={(selectedOption) => {
                                    if (!selectedOption || Array.isArray(selectedOption)) {
                                      field.onChange(null);
                                      return;
                                    }
                                    field.onChange({
                                      value: selectedOption.value,
                                      label: selectedOption.label,
                                    });
                                  }}
                                  isDisabled={isReadOnly}
                                  placeholder="Start typing or select from list..."
                                />
                              )}
                            />
                          </Field>
                          <Field>
                            <Label>Number of Shifts</Label>
                            <Controller
                              name="num_shifts"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  value={field.value?.toString() ?? ''}
                                  onChange={(e) => {
                                    const value =
                                      e.target.value === '' ? null : Number(e.target.value);
                                    field.onChange(value);
                                  }}
                                  placeholder="How many shifts do you need to fill?"
                                  disabled={isReadOnly}
                                />
                              )}
                            />
                          </Field>
                        </div>
                        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                          <Field>
                            <Label>Job Requisition Key</Label>
                            <Input
                              {...register('job_requisition_key')}
                              placeholder="ID for tracking a related external job requisition..."
                              disabled={isReadOnly}
                            />
                          </Field>
                          <Field>
                            <Label>Job Requisition URL</Label>
                            <Input
                              {...register('job_requisition_url')}
                              placeholder="https://www.example.com"
                              disabled={isReadOnly}
                            />
                          </Field>
                        </div>
                        <div className="tw:mb-4">
                          <Field>
                            <Label>Description</Label>
                            <Textarea
                              {...register('description')}
                              placeholder="Brief description of the opportunity..."
                              maxLength={255}
                              disabled={isReadOnly}
                            />
                            <div className="tw:flex tw:justify-end tw:text-sm tw:text-zinc-500 tw:mt-1 tw:dark:text-zinc-400">
                              {watch('description')?.length || 0}/255
                            </div>
                          </Field>
                        </div>
                        <div className="tw:mb-4">
                          <Field>
                            <Label>Additional Facilities</Label>
                            <Description>
                              Are there related facilities you wish to connect the provider with as
                              part of this need?
                            </Description>
                            <Controller
                              name="alternate_facilities"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  styles={reactSelectReset}
                                  placeholder="Start typing or select from list..."
                                  options={filteredFacilities
                                    .filter(
                                      (fac) =>
                                        !(field.value || []).some((sel: any) => sel.id === fac.id)
                                    )
                                    .map((facility) => ({
                                      value: facility,
                                      label: facility.name,
                                    }))}
                                  value={
                                    selectedAdditionalFacility
                                      ? {
                                          value: selectedAdditionalFacility,
                                          label: selectedAdditionalFacility.name,
                                        }
                                      : null
                                  }
                                  onChange={(selectedOption) => {
                                    if (
                                      selectedOption?.value &&
                                      !(field.value || []).some(
                                        (f: any) => f.id === selectedOption.value.id
                                      )
                                    ) {
                                      const currentValue = field.value || [];
                                      field.onChange([...currentValue, selectedOption.value]);
                                    }
                                    setSelectedAdditionalFacility(null);
                                  }}
                                  isDisabled={isReadOnly}
                                />
                              )}
                            />
                            <Controller
                              name="alternate_facilities"
                              control={control}
                              render={({ field }) => (
                                <div className="tw:space-y-2 tw:mt-2">
                                  {(field.value || []).map((facility: any) => (
                                    <div
                                      key={facility.id}
                                      className="tw:flex tw:items-center tw:gap-2 tw:p-2 tw:bg-brand-green-0/5 tw:border tw:border-brand-green-0 tw:rounded-lg tw:dark:border-brand-green-1 tw:dark:bg-white/5"
                                    >
                                      {!isReadOnly && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const currentValue = field.value || [];
                                            field.onChange(
                                              currentValue.filter((f: any) => f.id !== facility.id)
                                            );
                                          }}
                                          className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90"
                                        >
                                          <i className="fat fa-xmark fa-md tw:h-4 tw:w-4 tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90" />
                                        </button>
                                      )}
                                      <span className="tw:flex-1 tw:text-brand-green-0 tw:dark:text-brand-green-1">
                                        {facility.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            />
                          </Field>
                        </div>

                        {/* Linked Opportunities */}
                        {(isEditMode || isViewMode) && existingNeed?.linked_opportunities && (
                          <div className="tw:mt-4 tw:pt-4 tw:border-t tw:border-zinc-200 tw:dark:border-zinc-600">
                            <div className="tw:font-semibold tw:text-sm tw:text-zinc-900 tw:dark:text-zinc-100 tw:mb-2">
                              Linked Opportunities
                            </div>
                            <div>
                              {existingNeed.linked_opportunities.length > 0 ? (
                                <div className="tw:space-y-2">
                                  {existingNeed.linked_opportunities.map((opportunity) => (
                                    <div
                                      key={opportunity.id}
                                      className="tw:flex tw:items-center tw:justify-between tw:py-1"
                                    >
                                      <button
                                        type="button"
                                        onClick={() =>
                                          navigate(
                                            `/app/organization/opportunities/${opportunity.id}/edit`
                                          )
                                        }
                                        className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:text-left tw:truncate tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                                        title="View opportunity"
                                      >
                                        {opportunity.code || 'Untitled opportunity'}
                                      </button>
                                      <span className="tw:ml-2 tw:inline-flex tw:items-center">
                                        <StatusPill
                                          scope={WorkflowScopes.Opportunity}
                                          status={opportunity.current_status || 'Unknown'}
                                        />
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
                                  No linked opportunities.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </Fieldset>
                    </section>

                    {/* Professional Requirements Section */}
                    <section className={SECTION_CLASSES}>
                      <SectionHeader
                        headerText="Professional Requirements"
                        icon={
                          <i className="fas fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                        }
                      />
                      <Fieldset>
                        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-4">
                          <Field>
                            <Label>
                              Specialty<span className="tw:text-stoplight-danger">*</span>
                            </Label>
                            <Description>Select one</Description>
                            <Controller
                              name="specialty"
                              control={control}
                              render={({ field }) => (
                                <DropdownSelect
                                  allOptions={specialtySelectOptions}
                                  value={field.value}
                                  onChange={(selectedOption) => {
                                    if (!selectedOption || Array.isArray(selectedOption)) {
                                      field.onChange(null);
                                      return;
                                    }
                                    field.onChange({
                                      value: selectedOption.value,
                                      label: selectedOption.label,
                                    });
                                  }}
                                  isDisabled={isReadOnly}
                                  placeholder="Start typing or select from list..."
                                />
                              )}
                            />
                            {errors.specialty && (
                              <FieldValidationErrorMessage message={errors.specialty.message} />
                            )}
                          </Field>
                          <Field>
                            <Label>
                              Degree<span className="tw:text-stoplight-danger">*</span>
                            </Label>
                            <Description>Select all that apply</Description>
                            <Controller
                              name="degrees"
                              control={control}
                              render={({ field }) => (
                                <DropdownSelect
                                  allOptions={degreeSelectOptions}
                                  value={field.value}
                                  onChange={(selectedOptions) =>
                                    field.onChange(
                                      selectedOptions && Array.isArray(selectedOptions)
                                        ? selectedOptions.map((opt) => ({
                                            value: opt.value,
                                            label: opt.label,
                                          }))
                                        : []
                                    )
                                  }
                                  isDisabled={isReadOnly}
                                  isMulti
                                  placeholder="Start typing or select from list..."
                                />
                              )}
                            />
                            {errors.degrees && (
                              <FieldValidationErrorMessage message={errors.degrees.message} />
                            )}
                          </Field>
                        </div>
                        <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:mb-4">
                          <Field className="tw:col-span-1">
                            <Label>Availability</Label>
                            <Description>Select all that apply</Description>
                            <Controller
                              name="work_types"
                              control={control}
                              render={({ field }) => (
                                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4 tw:w-full">
                                  {workTypeOptions?.map((option) => (
                                    <CheckButton
                                      key={`work-type-${option.value}`}
                                      value={option.value}
                                      label={option.value}
                                      description={option.description}
                                      name="workType"
                                      checked={field.value?.includes(option.value as WorkTypeEnum)}
                                      onClick={() => {
                                        if (!isReadOnly) {
                                          const newValue = field.value.includes(
                                            option.value as WorkTypeEnum
                                          )
                                            ? field.value.filter((type) => type !== option.value)
                                            : [...field.value, option.value];
                                          field.onChange(newValue);
                                        }
                                      }}
                                      disabled={isReadOnly}
                                    />
                                  ))}
                                </div>
                              )}
                            />
                          </Field>
                        </div>
                      </Fieldset>
                    </section>
                    {/* Shift Types Section */}
                    <section className={SECTION_CLASSES}>
                      <SectionHeader
                        headerText="Shift Types"
                        icon={
                          <i className="fas fa-cog tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                        }
                      />
                      <Fieldset>
                        <div className="tw:grid tw:grid-cols-1 tw:mb-4">
                          <Field>
                            <Description>Click to select the shift types for this need</Description>
                            <Controller
                              name="shift_rate_types"
                              control={control}
                              render={({ field }) => (
                                <ShiftTypeSelector
                                  options={shiftTypeOptions}
                                  initialValue={field.value}
                                  onChange={(selectedTypes) => {
                                    if (!isReadOnly) {
                                      field.onChange(selectedTypes);
                                    }
                                  }}
                                  disabled={isReadOnly}
                                />
                              )}
                            />
                            {/* Individual rate input fields */}
                            <div
                              className={`tw:grid tw:gap-4 tw:w-full tw:mt-6 sm:tw:grid-cols-${shiftTypeOptions.length} tw:grid-cols-1`}
                              style={{
                                gridTemplateColumns: `repeat(${shiftTypeOptions.length}, 1fr)`,
                              }}
                            >
                              {shiftTypeOptions.map((opt) => {
                                const selected = watch('shift_rate_types')?.includes(opt.key);
                                const rateFieldName = `max_rate_${opt.key}`;

                                return selected ? (
                                  <Field key={opt.key} className="tw:mb-0">
                                    <Label>Maximum {opt.label} Rate</Label>
                                    <Controller
                                      name={rateFieldName as any}
                                      control={control}
                                      render={({ field }) => (
                                        <div className="tw:relative">
                                          <Input
                                            type="text"
                                            inputMode="numeric"
                                            value={field.value?.toString() ?? ''}
                                            onChange={(e) => {
                                              const value =
                                                e.target.value === ''
                                                  ? null
                                                  : Number(e.target.value);
                                              field.onChange(value);
                                            }}
                                            disabled={isReadOnly}
                                          />
                                          <span className="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-gray-500 tw:dark:text-zinc-300 tw:text-sm">
                                            /hr
                                          </span>
                                        </div>
                                      )}
                                    />
                                  </Field>
                                ) : (
                                  <div key={opt.key} />
                                );
                              })}
                            </div>
                          </Field>
                        </div>
                      </Fieldset>
                    </section>
                  </div>
                  <div className="tw:flex tw:flex-wrap tw:items-center tw:align-center tw:justify-end tw:gap-2 tw:mt-8">
                    {actionButtonsElement}
                  </div>
                </form>
              </main>
            </div>
          </div>
        </>
      )}

      <UnsavedChangesFormPrompt hasUnsavedChanges={form.formState.isDirty && !isSubmitting} />
    </>
  );
};

export default NeedForm;
